import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { workspacePath } from '../../workspace-path';
import { buttonClasses } from '@forecastify/ui';
import { useT, type MessageKey } from '../../i18n';
import { useSession } from '../../session';
import { SIGN_IN_PATH, SIGN_UP_PATH } from '../../routes';
import { readAuthFragment, type AuthOutcome } from './auth-fragment';
import { AuthFrame } from './auth-frame';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Where a confirmation link lands. Two producers, and one already broken.   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `AUTH_PATHS.verifyEmail` has **two** senders: `POST /auth/sign-up` (S2's own flow) and C6's
 * `POST /auth/email` address change. The second has been pointing here since C6 shipped — it always
 * passed its `redirect_to` correctly — so **every email-change confirmation link in this product
 * has been landing on the catch-all**, which renders nothing anybody can act on. That is WP34-2's
 * exact failure, and it survived because the flow ends in a mailbox and nothing in the suite opens
 * one.
 *
 * ⚠️⚠️ **THIS PAGE DOES NOT SIGN ANYBODY IN, AND THE FRAGMENT IS THE REASON IT IS TEMPTING TO.**
 * GoTrue hands back a live `access_token` and `refresh_token` in the URL. Adopting them would be
 * three lines and it would put a session into this application that **our registry has never
 * seen** — and `request-context.ts` is unambiguous about what that means: *"a session we have never
 * seen means the registry was bypassed, which is the one thing it exists to make impossible.
 * Registering it here would launder exactly that."* Sign-out, revocation and the devices screen all
 * read that registry. So the page confirms, and the person signs in through our API, which mints a
 * session we know about. One extra password entry is the price and it is worth it.
 *
 * ⚠️ **R-11 agrees from the other side**: the access token lives in memory only and the refresh
 * token is an httpOnly cookie *our* API sets. There is nowhere legitimate to put a token that
 * arrived in a URL.
 */

/** One sentence per outcome. Rule 5's shape, applied to a fragment rather than an envelope. */
const TITLE: Readonly<Record<string, MessageKey>> = {
  'confirmed:signup': 'verify.signup.title',
  'confirmed:email_change': 'verify.emailChange.title',
  'confirmed:other': 'verify.other.title',
  dead: 'verify.dead.title',
  error: 'verify.error.title',
  unknown: 'verify.unknown.title',
};

const BODY: Readonly<Record<string, MessageKey>> = {
  'confirmed:signup': 'verify.signup.body',
  'confirmed:email_change': 'verify.emailChange.body',
  'confirmed:other': 'verify.other.body',
  dead: 'verify.dead.body',
  error: 'verify.error.body',
  unknown: 'verify.unknown.body',
};

const keyOf = (outcome: AuthOutcome): string =>
  outcome.outcome === 'confirmed' ? `confirmed:${outcome.kind}` : outcome.outcome;

export function VerifyEmailScreen() {
  const t = useT();
  const session = useSession();

  /**
   * ⚠️⚠️ **READ ONCE INTO STATE, BEFORE THE FRAGMENT IS WIPED.** The effect below removes it from
   * the address bar, so anything re-reading `location.hash` on a later render would see an empty
   * string and report `unknown` — a page that says "we confirmed you" and then contradicts itself
   * on the next paint. `useState`'s initialiser runs once, which is exactly the lifetime wanted.
   */
  const [outcome] = useState<AuthOutcome>(() => readAuthFragment(window.location.hash));

  /**
   * ⚠️⚠️ **THE CREDENTIALS ARE SCRUBBED FROM THE URL, AND THIS IS NOT TIDINESS.** That fragment
   * holds a live access token and a refresh token. Left in place they go into browser history, into
   * any screenshot of the page, into whatever a person pastes when they ask a colleague "is this
   * normal?", and into the address bar of a shared machine. `replaceState` rather than `pushState`,
   * so the Back button does not restore them.
   *
   * ⚠️ It runs regardless of outcome — a refused link's fragment is harmless, and a branch that
   * scrubs only on success is a branch that stops scrubbing the moment the outcomes change.
   */
  useEffect(() => {
    if (window.location.hash === '') return;
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  }, []);

  const key = keyOf(outcome);

  /**
   * ⚠️⚠️ **THE CONTROLS ARE `<Link>`s WEARING THE BUTTON'S SKIN, VIA `buttonClasses`.** Every
   * control on this page navigates and nothing on it submits, so `button.tsx`'s rule — *"a control
   * that NAVIGATES is not a button"* — disqualifies `Button` for all three. They still have to LOOK
   * like the page's primary action, because sending somebody onward is this page's entire purpose.
   *
   * ⚠️ **`buttonClasses` rather than a hand-written class string, which is what this file had
   * first.** Its own docstring is why: copying the variant's classes *"then drifts from the
   * variants"*, and the alternative it names — a `<button>` wrapped around an `<a>` — is
   * `nested-interactive`, a WCAG failure the outgoing project shipped. One helper, one skin, either
   * element. `w-full`, matching the frame's own submit buttons.
   */
  const primary = buttonClasses('default', 'lg', 'w-full');
  const secondary = buttonClasses('outline', 'lg', 'w-full');

  const held = session.workspaces.find((entry) => entry.id === session.workspaceId) ?? null;
  const accountHref = held === null ? '/' : workspacePath(held.slug, '/account');

  return (
    <AuthFrame screen="verify-email" title={t(TITLE[key] ?? 'verify.unknown.title')}>
      <p className="text-[13px] leading-relaxed">{t(BODY[key] ?? 'verify.unknown.body')}</p>

      {outcome.outcome === 'error' && (
        /* The code, because a support conversation that starts from a string beats one that starts
           from "it did not work" — the same argument `screen-state.tsx` makes for a request id. */
        <p className="text-muted-foreground text-[11.5px]">{outcome.code}</p>
      )}

      {/**
       * ⚠️ **The action depends on whether they already have a session.** An email change is
       * confirmed by somebody who is signed in, and sending them to a login form they do not need is
       * the *"control that does nothing"* failure in reverse — it asks for work that accomplishes
       * nothing. A signup confirmation is the opposite: there is no session, and signing in is the
       * entire next step.
       */}
      {session.status === 'signed-in' ? (
        /**
         * ⚠️⚠️ **R-38 · THE ONE LINK IN THE APP THAT CANNOT TAKE THE WORKSPACE FROM THE ADDRESS.**
         * This screen lives at `/auth/verify-email`, a ROOT route — a mailed link has to work for
         * somebody with no session at all, so it carries no workspace segment and
         * `useWorkspacePath()` would have nothing to read. The workspace comes from the SESSION
         * instead, which is available precisely in this branch, because the branch condition is
         * that they are signed in.
         *
         * ⚠️ **`'/'` when the session holds no workspace**, rather than a bare `/account` that
         * would resolve to a workspace called `account`. `resolveWorkspaceAddress` sends `/` to
         * wherever they belong, which is the honest answer when this screen cannot say.
         */
        <Link to={accountHref} className={primary}>
          {t('verify.toAccount')}
        </Link>
      ) : (
        <Link to={SIGN_IN_PATH} className={primary}>
          {t('verify.toSignIn')}
        </Link>
      )}

      {/* ⚠️ Offered ONLY for a dead link, and it is "sign up again" rather than "resend". GoTrue
          cannot tell an expired link from one already used successfully, so the commonest cause of
          landing here is a link that already worked — and there is no resend endpoint to put behind a
          "send it again" button anyway. */}
      {outcome.outcome === 'dead' && session.status !== 'signed-in' && (
        <Link to={SIGN_UP_PATH} className={secondary}>
          {t('verify.toSignUp')}
        </Link>
      )}
    </AuthFrame>
  );
}
